'use client';

// Customer-facing self-serve booking page (cal.<domain> and cal.<domain>/<shortId>).
// PUBLIC: no session, no useAuthGuard, no app chrome. Shows only what a
// customer may see — real openings and their own details, never closer or
// campaign internals (Trello #464).
//
// Deliberately LIGHT, unlike the dark staff app: this is a customer surface,
// the same concession the eSign paper sheet makes (esign-signing-page.tsx).
// It still uses the token ramp + corner-brand so the brand signature carries.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { ApiError } from '@/lib/api/client';
import { bookingPortalApi } from '@/lib/api/booking-portal';
import { useBrandName, useBrandTitle } from '@/lib/api/brand';
import { firstDayOfWeek, formatConfirmedStart, monthGrid, monthLabel, slotTimeLabel, weekdayInitials } from '@/lib/booking-portal-calendar';
import type {
  BookingPortalClientEvent,
  BookingPortalContextResponse,
  BookingPortalSlotsResponse,
} from '../../../shared/types/booking-portal';
import { bookingPortalTimezones, DEFAULT_TIMEZONE, timezoneAbbreviation } from '@/lib/timezone';
import { Spinner } from '@/ui';
import { cn } from '@/lib/cn';

// Light-surface control styling, local to this page (the @/ui primitives are
// dark-app-only by design). Width is deliberately NOT in the base: the select
// sizes to its content, and `w-full` vs `w-auto` in one class list is decided
// by stylesheet order, not attribute order.
const CONTROL_BASE =
  'corner-brand-sm border border-zinc-200 bg-white px-3 text-sm text-zinc-950 placeholder:text-zinc-400 transition-colors outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-50';
const CONTROL = `${CONTROL_BASE} h-10 w-full`;

function browserTimezone(): string {
  if (typeof Intl === 'undefined') {
    return DEFAULT_TIMEZONE;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** "GMT+2" / "GMT-5" — always short, and meaningful to a visitor anywhere. */
function zoneOffsetLabel(timezone: string): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

/**
 * The parenthetical in the timezone picker. US zones have familiar
 * abbreviations (CDT, EDT); most others don't, and Intl falls back to a long
 * generic name — "Oslo (Norway Time)" overflowed the control. Anything that
 * isn't a compact abbreviation becomes a GMT offset instead.
 */
function zoneSuffix(timezone: string): string {
  let abbreviation: string;
  try {
    abbreviation = timezoneAbbreviation(timezone);
  } catch {
    abbreviation = '';
  }
  if (!abbreviation || abbreviation.length > 5 || abbreviation.includes(' ')) {
    return zoneOffsetLabel(timezone);
  }
  return abbreviation;
}

function dateKeyInTimezone(instantMs: number, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(instantMs));
}

function shiftMonth(yearMonth: string, delta: number): string {
  const [year, month] = yearMonth.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SlotsState = { loading: boolean; error: string | null; data: BookingPortalSlotsResponse | null };

function LightField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-sm font-medium text-zinc-700">
        {label}
        {required ? <span aria-hidden className="text-accent"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

/**
 * Interests picker. A chip cloud of eleven options (one of them a full line of
 * ad networks) dominated the form visually, so the options live behind a
 * dropdown and the field reads as one control like the ones around it.
 */
function InterestSelect({
  options,
  selected,
  onChange,
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // One pick reads better as itself; more than one would wrap, so count it.
  const summary =
    selected.length === 0 ? 'Select what applies' : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cn(CONTROL, 'flex items-center justify-between gap-2 text-left', selected.length === 0 && 'text-zinc-400')}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown
          aria-hidden
          className={cn('size-4 shrink-0 text-zinc-400 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open ? (
        <div
          role="listbox"
          aria-multiselectable
          aria-label="What are you interested in?"
          className="corner-brand-sm absolute z-20 mt-1 max-h-64 w-full overflow-auto border border-zinc-200 bg-white py-1 shadow-lg"
        >
          {options.map((option) => {
            const isSelected = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() =>
                  onChange(isSelected ? selected.filter((value) => value !== option) : [...selected, option])
                }
                className="flex w-full items-start gap-2.5 px-3 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-50"
              >
                <span
                  aria-hidden
                  className={cn(
                    'corner-brand-xs mt-0.5 flex size-4 shrink-0 items-center justify-center border',
                    isSelected ? 'border-accent bg-accent text-white' : 'border-zinc-300 bg-white'
                  )}
                >
                  {isSelected ? <Check className="size-3" /> : null}
                </span>
                {option}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// Full-page terminal state (light counterpart of PublicStatusPage).
function PortalStatus({
  tone,
  title,
  brand,
  children,
}: {
  tone: 'success' | 'warning' | 'danger';
  title: string;
  brand: string | undefined;
  children: React.ReactNode;
}) {
  const brandName = useBrandName(brand);
  const circle = tone === 'success' ? 'bg-success/15 text-emerald-600' : tone === 'warning' ? 'bg-warning/15 text-amber-600' : 'bg-danger/15 text-rose-600';
  return (
    <div className="grid min-h-screen place-items-center bg-zinc-50 px-5 py-8">
      <div className="w-full max-w-md">
        <div className="corner-brand border border-zinc-200 bg-white p-10 text-center shadow-sm">
          <div className={cn('mx-auto flex size-12 items-center justify-center rounded-full', circle)}>
            <Check className={cn('size-6', tone !== 'success' && 'hidden')} aria-hidden />
            {tone !== 'success' ? <X className="size-6" aria-hidden /> : null}
          </div>
          <h1 className="mt-5 text-xl font-semibold text-zinc-950">{title}</h1>
          <p className="mt-2.5 text-sm leading-6 text-zinc-600">{children}</p>
        </div>
        <p className="mt-6 text-center text-xs text-zinc-400">{brandName}</p>
      </div>
    </div>
  );
}

export function BookingPortalPage({ shortId }: { shortId: string | null }) {
  const [context, setContext] = useState<BookingPortalContextResponse | null>(null);
  const [contextError, setContextError] = useState<{ status: number | null; message: string } | null>(null);

  const [timezone, setTimezone] = useState<string>(() => browserTimezone());
  const todayKey = useMemo(() => dateKeyInTimezone(Date.now(), timezone), [timezone]);
  const [monthCursor, setMonthCursor] = useState<string>(() => todayKey.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState<string>(todayKey);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slotsState, setSlotsState] = useState<SlotsState>({ loading: true, error: null, data: null });
  // Booking bounds from the latest slots response — bound the calendar.
  const [bounds, setBounds] = useState<{ min: string | null; max: string | null }>({ min: null, max: null });

  const [businessName, setBusinessName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [needs, setNeeds] = useState('');
  const [showExtraAttendee, setShowExtraAttendee] = useState(false);
  const [extraName, setExtraName] = useState('');
  const [extraEmail, setExtraEmail] = useState('');
  const [extraPhone, setExtraPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<'booked' | 'alreadyBooked' | null>(null);
  const [confirmedStartAt, setConfirmedStartAt] = useState<string | null>(null);
  const prefillApplied = useRef(false);
  useBrandTitle('Book a meeting', context?.brand ?? '');

  const track = useCallback(
    (event: BookingPortalClientEvent, detail?: Record<string, unknown>) =>
      bookingPortalApi.event({ shortId, event, detail: detail ?? null }),
    [shortId]
  );

  useEffect(() => {
    let cancelled = false;
    track('page_view', {
      path: window.location.pathname,
      referrer: document.referrer || null,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: `${window.screen.width}x${window.screen.height}`,
    });
    bookingPortalApi
      .context(shortId)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setContext(payload);
        if (payload.prefill && !prefillApplied.current) {
          prefillApplied.current = true;
          setBusinessName(payload.prefill.businessName ?? '');
          setName(payload.prefill.name ?? '');
          setEmail(payload.prefill.email ?? '');
          setPhone(payload.prefill.phone ?? '');
        }
      })
      .catch((error: unknown) => {
        const status = error instanceof ApiError ? error.status : null;
        const message = error instanceof Error ? error.message : 'Something went wrong.';
        track('client_error', { stage: 'context', status, message });
        if (!cancelled) {
          setContextError({ status, message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [shortId, track]);

  const loadSlots = useCallback(
    (date: string) => {
      setSlotsState({ loading: true, error: null, data: null });
      bookingPortalApi
        .slots({ shortId, date, timezone })
        .then((payload) => {
          setSlotsState({ loading: false, error: null, data: payload });
          setBounds({ min: payload.minBookableInstant, max: payload.maxBookableInstant });
        })
        .catch((error: unknown) => {
          const message = error instanceof Error ? error.message : 'Could not load available times.';
          track('client_error', { stage: 'slots', date, timezone, message });
          setSlotsState({ loading: false, error: message, data: null });
        });
    },
    [shortId, timezone, track]
  );

  useEffect(() => {
    if (context?.enabled) {
      setSelectedSlot(null);
      loadSlots(selectedDate);
    }
  }, [context?.enabled, selectedDate, loadSlots]);

  // A timezone change moves "today": snap the selection back inside range.
  useEffect(() => {
    setSelectedDate((current) => (current < todayKey ? todayKey : current));
  }, [todayKey]);

  const minDateKey = useMemo(
    () => (bounds.min ? dateKeyInTimezone(Math.max(Date.parse(bounds.min), Date.now()), timezone) : todayKey),
    [bounds.min, timezone, todayKey]
  );
  const maxDateKey = useMemo(
    () => (bounds.max ? dateKeyInTimezone(Date.parse(bounds.max), timezone) : null),
    [bounds.max, timezone]
  );

  // Customer-facing zone list (bookingPortalTimezones): the region's customer
  // zones, not the staff pick list with its offshore staff zones — wrong
  // audience here. The visitor's own zone is auto-detected and added when it
  // isn't one of these.
  const timezoneOptions = useMemo(() => {
    const options = context ? bookingPortalTimezones(context) : [];
    if (!options.some((option) => option.value === timezone)) {
      options.unshift({ value: timezone, label: timezone.split('/').pop()?.replace(/_/g, ' ') || timezone });
    }
    return options.map((option) => {
      const suffix = zoneSuffix(option.value);
      return {
        value: option.value,
        label: suffix && suffix !== option.label ? `${option.label} (${suffix})` : option.label,
      };
    });
  }, [context, timezone]);

  const detailsValid =
    businessName.trim().length > 0 &&
    name.trim().length > 0 &&
    EMAIL_PATTERN.test(email.trim()) &&
    phone.trim().length >= 7;

  const submit = async () => {
    if (!selectedSlot || !detailsValid || submitting) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    track('submit_clicked', { startAt: selectedSlot, timezone });
    try {
      const payload = await bookingPortalApi.book({
        shortId,
        startAt: selectedSlot,
        timezone,
        businessName: businessName.trim(),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        interests,
        needs: needs.trim() || null,
        extraAttendee:
          showExtraAttendee && (extraName.trim() || extraEmail.trim() || extraPhone.trim())
            ? { name: extraName.trim() || null, email: extraEmail.trim() || null, phone: extraPhone.trim() || null }
            : null,
      });
      if (payload.alreadyBooked) {
        setResult('alreadyBooked');
      } else {
        setConfirmedStartAt(payload.meeting?.startAt ?? selectedSlot);
        setResult('booked');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'The booking could not be completed.';
      track('client_error', { stage: 'book', startAt: selectedSlot, message });
      if (error instanceof ApiError && error.message === 'slot_taken') {
        setSelectedSlot(null);
        setSubmitError('That time was just taken — please pick another one.');
        loadSlots(selectedDate);
      } else {
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Terminal states ────────────────────────────────────────────────────────

  if (result === 'booked') {
    return (
      <PortalStatus tone="success" title="You're booked" brand={context?.brand}>
        {confirmedStartAt && context
          ? `Your meeting is confirmed for ${formatConfirmedStart(confirmedStartAt, timezone, context.locale)}. A calendar invitation is on its way to ${email.trim()}.`
          : `Your meeting is confirmed. A calendar invitation is on its way to ${email.trim()}.`}
      </PortalStatus>
    );
  }
  if (result === 'alreadyBooked') {
    return (
      <PortalStatus tone="warning" title="You already have a meeting" brand={context?.brand}>
        It looks like a meeting is already scheduled with you. Check your email for the invitation — or reply to it if
        you need a different time.
      </PortalStatus>
    );
  }
  if (contextError) {
    return (
      <PortalStatus tone="danger" title={contextError.status === 404 ? 'Link not found' : 'Something went wrong'} brand={context?.brand}>
        {contextError.status === 404
          ? 'This booking link is no longer valid. Please reach out to your contact for a fresh one.'
          : 'The booking page could not be loaded. Please try again in a moment.'}
      </PortalStatus>
    );
  }
  if (!context) {
    return (
      <div className="grid min-h-screen place-items-center bg-zinc-50">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (!context.enabled) {
    return (
      <PortalStatus tone="warning" title="Booking is unavailable" brand={context.brand}>
        Online booking is not open right now. Please reach out to your contact directly to schedule a meeting.
      </PortalStatus>
    );
  }

  // ── Booking form ───────────────────────────────────────────────────────────

  const firstDay = firstDayOfWeek(context.locale);
  const weeks = monthGrid(monthCursor, firstDay);
  const slots = slotsState.data?.slots ?? [];
  const canGoPrev = monthCursor > minDateKey.slice(0, 7);
  const canGoNext = !maxDateKey || monthCursor < maxDateKey.slice(0, 7);

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-10 text-zinc-950 sm:py-16">
      <div className="mx-auto w-full max-w-4xl">
        <header className="mb-8 text-center">
          <p className="text-sm font-medium text-accent">{context.brand}</p>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tight text-zinc-950">Book a meeting</h1>
          <p className="mt-2 text-sm text-zinc-500">
            {context.durationMinutes} minutes · video call · we&apos;ll send the invite by email
          </p>
        </header>

        <div className="corner-brand border border-zinc-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-2 md:divide-x md:divide-zinc-100">
            {/* Date + time */}
            <section className="p-6 sm:p-8">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h2 className="text-base font-semibold text-zinc-900">Pick a time</h2>
                <span className="relative inline-flex min-w-0">
                  <select
                    aria-label="Timezone"
                    className={cn(CONTROL_BASE, 'h-9 w-auto max-w-[13rem] min-w-0 appearance-none pr-7 text-zinc-600')}
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value)}
                  >
                    {timezoneOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2 text-zinc-400"
                  />
                </span>
              </div>

              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Previous month"
                  disabled={!canGoPrev}
                  onClick={() => setMonthCursor((cursor) => shiftMonth(cursor, -1))}
                  className="corner-brand-sm p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="size-4" aria-hidden />
                </button>
                <span className="text-sm font-semibold text-zinc-900">{monthLabel(monthCursor, context.locale)}</span>
                <button
                  type="button"
                  aria-label="Next month"
                  disabled={!canGoNext}
                  onClick={() => setMonthCursor((cursor) => shiftMonth(cursor, 1))}
                  className="corner-brand-sm p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="size-4" aria-hidden />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center">
                {weekdayInitials(context.locale, firstDay).map((day, index) => (
                  <span key={`${day}-${index}`} className="py-1.5 text-xs font-medium text-zinc-400">
                    {day}
                  </span>
                ))}
                {weeks.flat().map((dateKey, index) => {
                  if (!dateKey) {
                    return <span key={`pad-${index}`} />;
                  }
                  const disabled = dateKey < minDateKey || Boolean(maxDateKey && dateKey > maxDateKey);
                  const selected = dateKey === selectedDate;
                  return (
                    <button
                      key={dateKey}
                      type="button"
                      disabled={disabled}
                      onClick={() => setSelectedDate(dateKey)}
                      className={cn(
                        'mx-auto flex size-9 items-center justify-center rounded-full text-sm tabular-nums transition-colors',
                        selected
                          ? 'bg-accent font-semibold text-white'
                          : disabled
                            ? 'cursor-not-allowed text-zinc-300'
                            : 'font-medium text-zinc-700 hover:bg-zinc-100'
                      )}
                    >
                      {Number(dateKey.slice(8))}
                    </button>
                  );
                })}
              </div>

              <div className="mt-5">
                {slotsState.loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner />
                  </div>
                ) : slotsState.error ? (
                  <p className="py-6 text-center text-sm text-rose-600">{slotsState.error}</p>
                ) : slots.length === 0 ? (
                  <p className="py-6 text-center text-sm text-zinc-500">No open times that day — try another date.</p>
                ) : (
                  <div className="grid max-h-52 grid-cols-3 gap-2 overflow-y-auto pr-1">
                    {slots.map((slot) => {
                      const selected = selectedSlot === slot.startAt;
                      return (
                        <button
                          key={slot.startAt}
                          type="button"
                          onClick={() => {
                            setSelectedSlot(slot.startAt);
                            track('slot_selected', { startAt: slot.startAt, date: selectedDate, timezone });
                          }}
                          className={cn(
                            'corner-brand-sm border py-2 text-sm font-medium tabular-nums transition-colors',
                            selected
                              ? 'border-accent bg-accent text-white'
                              : 'border-zinc-200 text-zinc-700 hover:border-accent hover:text-accent'
                          )}
                        >
                          {slotTimeLabel(slot.localTime, context.locale)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* Details */}
            <section className="flex flex-col gap-4 border-t border-zinc-100 p-6 sm:p-8 md:border-t-0">
              <h2 className="text-base font-semibold text-zinc-900">Your details</h2>
              <LightField label="Business name" required>
                <input
                  className={CONTROL}
                  value={businessName}
                  onChange={(event) => setBusinessName(event.target.value)}
                  autoComplete="organization"
                />
              </LightField>
              <LightField label="Your name" required>
                <input
                  className={CONTROL}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                />
              </LightField>
              <div className="grid gap-4 sm:grid-cols-2">
                <LightField label="Email" required>
                  <input
                    className={CONTROL}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                  />
                </LightField>
                <LightField label="Phone" required>
                  <input
                    className={CONTROL}
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="+1 (555) 123-4567"
                    autoComplete="tel"
                  />
                </LightField>
              </div>

              <LightField label="What are you interested in?">
                <InterestSelect options={context.interests} selected={interests} onChange={setInterests} />
              </LightField>

              <LightField label="Anything we should know?">
                <textarea
                  className={cn(CONTROL, 'h-auto min-h-20 resize-y py-2 leading-6')}
                  value={needs}
                  onChange={(event) => setNeeds(event.target.value)}
                  maxLength={2000}
                  placeholder="Tell us a bit about what you're looking for"
                />
              </LightField>

              {showExtraAttendee ? (
                <div className="corner-brand-sm border border-zinc-200 bg-zinc-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-700">Additional attendee</span>
                    <button
                      type="button"
                      aria-label="Remove attendee"
                      onClick={() => setShowExtraAttendee(false)}
                      className="text-zinc-400 transition-colors hover:text-zinc-700"
                    >
                      <X className="size-4" aria-hidden />
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      className={CONTROL}
                      placeholder="Name"
                      value={extraName}
                      onChange={(event) => setExtraName(event.target.value)}
                    />
                    <input
                      className={CONTROL}
                      type="email"
                      placeholder="Email"
                      value={extraEmail}
                      onChange={(event) => setExtraEmail(event.target.value)}
                    />
                    <input
                      className={CONTROL}
                      type="tel"
                      placeholder="Phone (optional)"
                      value={extraPhone}
                      onChange={(event) => setExtraPhone(event.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowExtraAttendee(true)}
                  className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-accent transition-colors hover:text-accent-hover"
                >
                  <Plus className="size-3.5" aria-hidden />
                  Add another attendee
                </button>
              )}
            </section>
          </div>

          <div className="border-t border-zinc-100 p-6 sm:px-8">
            {selectedSlot ? (
              <p className="mb-4 text-center text-sm font-medium text-zinc-900">
                {formatConfirmedStart(selectedSlot, timezone, context.locale)}
              </p>
            ) : (
              <p className="mb-4 text-center text-sm text-zinc-400">Pick a time to continue.</p>
            )}
            {submitError ? <p className="mb-4 text-center text-sm text-rose-600">{submitError}</p> : null}
            <button
              type="button"
              disabled={!selectedSlot || !detailsValid || submitting}
              onClick={() => void submit()}
              className="corner-brand-sm inline-flex h-11 w-full items-center justify-center gap-2 bg-accent text-sm font-semibold text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? <Spinner className="size-4 text-white" /> : null}
              Confirm booking
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-zinc-400">{context.brand}</p>
      </div>
    </div>
  );
}
